import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Newspaper } from "lucide-react";

function formatCachedAt(v) {
  if (!v) return "never fetched";
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-GB");
}

export function BlogCachePanel() {
  const [state, setState] = useState({ count: 0, cached_at: null, error: null, loading: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/public/blog/articles");
      setState({ count: r.data.count || 0, cached_at: r.data.cached_at, error: r.data.error, loading: false });
    } catch (e) {
      setState({ count: 0, cached_at: null, error: e?.response?.data?.detail || "Could not load blog cache", loading: false });
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/blog/refresh");
      if (r.data.error) toast.error(`Refresh finished with a warning: ${r.data.error}`);
      else toast.success(`Blog cache refreshed — ${r.data.count} article${r.data.count === 1 ? "" : "s"} loaded from BabyLoveGrowth`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="blog-cache-panel" className="max-w-3xl">
      <h2 className="text-xl font-black tracking-tight mb-1 flex items-center gap-2"><Newspaper size={20} className="text-[#C79A3B]"/> Blog cache (BabyLoveGrowth)</h2>
      <p className="text-sm text-[#6B7280] mb-6">Blog articles are fetched from BabyLoveGrowth and cached for 24 hours to keep the site fast and stay within the API rate limit. Publish a new article there and hit <b>Refresh now</b> below to make it appear on <a href="/blog" target="_blank" rel="noreferrer" className="de-link inline-flex items-center gap-1">/blog <ExternalLink size={12}/></a> immediately.</p>

      <div className="border border-[#E5E7EB] bg-white p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div>
            <div className="label-eyebrow text-[#6B7280] mb-1">Articles cached</div>
            <div className="text-3xl font-black text-[#12263A]" data-testid="blog-cache-count">
              {state.loading ? "…" : state.count}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="label-eyebrow text-[#6B7280] mb-1">Last refresh</div>
            <div className="text-sm font-bold text-[#12263A]" data-testid="blog-cache-when">
              {state.loading ? "loading…" : formatCachedAt(state.cached_at)}
            </div>
            {state.error && !state.loading && (
              <div className="text-xs mt-2 text-[#8A3D0A] bg-[#FBE3D6] px-2 py-1 inline-block" data-testid="blog-cache-error">
                Warning: {state.error}
              </div>
            )}
          </div>
        </div>

        <button
          data-testid="blog-cache-refresh"
          onClick={refresh}
          disabled={busy}
          className="de-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""}/>
          {busy ? "Refreshing…" : "Refresh now"}
        </button>
      </div>
    </div>
  );
}
